import React, { useContext, useEffect, useState, useCallback } from 'react';
import TopNavbar from './TopNavbar';
import Navbar from './Navbar';
import TecnicaNavbar from './TecnicaNavbar';
import { esRutaTecnica } from '../pages/admin/tecnica/tecnicaRutas';
import '../css/Layout.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { NewContext } from '../utils/ContextProvider';
import { isTokenValid } from '../utils/auth';
import LoadingOverlay from './LoadingOverlay';
import useSessionTimeout from '../hooks/useSessionTimeout';
import SessionWarningModal from './SessionWarningModal';
import { getRefreshToken } from '../utils/peticionesApi';

/** La sesión del portal de legales es independiente de la de la app: no se borra al limpiar la de la app. */
const CLAVES_PORTAL_LEGALES = ['legales_token', 'legales_data'];

/**
 * Sin sesión válida de la app (no hay access_token o venció): limpia todo menos la sesión del portal de legales
 * (p. ej. un abogado externo, que nunca tiene access_token). Vencer no es cerrar sesión: el portal valida su propio
 * token. Los cierres de sesión sí borran las dos: el botón de la app (removeUserSession, utils/common.js) y la
 * inactividad de la app (handleLogout, abajo: sessionStorage.clear()).
 */
export const limpiarSesionDeLaApp = () => {
  const conservar = CLAVES_PORTAL_LEGALES.map((k) => [k, sessionStorage.getItem(k)]);
  sessionStorage.clear();
  conservar.forEach(([k, v]) => {
    if (v !== null) sessionStorage.setItem(k, v);
  });
};

const Layout = ({ children }) => {
  const location = useLocation();
  const { getToken, controlToken, flag, setStates } = useContext(NewContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const handleLogout = useCallback(() => {
    sessionStorage.clear();
    setStates();
    navigate('/');
  }, [navigate, setStates]);

  const handleTokenRefresh = useCallback(async () => {
    const { data } = await getRefreshToken();
    sessionStorage.setItem('access_token', data);
  }, []);

  const {
    showWarning,
    remainingFormatted,
    extendSession,
    logout,
    warningReason,
  } = useSessionTimeout({
    timeoutMinutes: 20,
    warningMinutes: 2,
    onTimeout: handleLogout,
    onTokenRefresh: handleTokenRefresh,
    enabled: !!getToken && location.pathname !== '/',
  });

  useEffect(() => {
    const token = sessionStorage.getItem('access_token');

    if (!token || !isTokenValid()) {
      limpiarSesionDeLaApp();
      navigate('/');
    } else {
      if (!getToken) {
        controlToken(token).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    }
  }, [getToken]);

  if (loading && location.pathname !== '/') return <LoadingOverlay isLoading={true} />;

  if (location.pathname === '/') return children;

  return (
    <div>
      <SessionWarningModal
        show={showWarning}
        remainingTime={remainingFormatted}
        onExtend={extendSession}
        onLogout={logout}
        reason={warningReason}
      />

      <TopNavbar />
      <div className="container-layout">
        {/* En Técnica el menú general se reemplaza por el del módulo, pero la sesión, el TopNavbar (empresa,
            renovación del token) y el colapso son los mismos: es un modo del Layout, no un portal aparte (plan §10). */}
        {esRutaTecnica(location.pathname) ? <TecnicaNavbar /> : <Navbar />}
        <div className={`${flag ? 'container-page' : 'container-page-close'}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
